import { useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { usePost } from 'lib/api';
import { apiMembers } from 'api/members';
import { apiSuppliers } from 'api/suppliers';
import { useNavigate } from 'react-router-dom';
import type { SignupResponse, UserAssociatedSupplierAccounts, LoginContext } from 'features/auth';
import { useLoginWithNavigation } from 'features/auth/hooks/useLoginWithNavigation';
import { useEvent } from 'lib/segment';
import { useUser } from 'features/auth';
import { USE_FEDERATED_AUTH } from 'config';

const SIGNUP_RESPONSE_KEY = ['supplier-signup-response'] as const;

export const useSupplierSignUp = (loginId?: string) => {
  const event = useEvent();
  const { data: userData } = useUser();
  const { selectCompany } = useLoginWithNavigation({
    onCompanySelectSuccess: () => {
      clearSignupResponse();
    },
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();

  const getSignupResponse = (): SignupResponse | undefined => {
    return queryClient.getQueryData(SIGNUP_RESPONSE_KEY) as SignupResponse | undefined;
  };

  const setSignupResponse = (response: SignupResponse) => {
    queryClient.setQueryData(SIGNUP_RESPONSE_KEY, response);
  };

  const clearSignupResponse = () => {
    queryClient.removeQueries({ queryKey: SIGNUP_RESPONSE_KEY });
  };

  const companySignIn = (organizationId: number, organizationName: string, userId?: string) => {
    const currentResponse = getSignupResponse();
    const userAssociation = currentResponse?.userAssociatedSupplierAccounts?.find(
      f => f.supplierId === organizationId
    );
    const loginContext: LoginContext = {
      id: userId ? userId : userAssociation?.userId || '',
      name: '',
      organizationType: 2,
      organizationName,
      email: currentResponse?.email || '',
      roleNames: [],
      designerOrganizationName: '',
      designerOrganizationId: userAssociation?.designerId || 0,
      organizationId,
    };
    selectCompany(loginContext);
  };

  const { mutate: signUp, isPending } = usePost<{ email: string; id: string }>(
    apiMembers.post.signup,
    false,
    [],
    {
      onSuccess(data, variables) {
        const res = data as { code: string; data: { userId: string } };
        if (res.code === 'signup_otp_sent') {
          const email = Array.isArray(variables.data)
            ? variables.data[0]?.email
            : variables.data?.email;
          setSignupResponse({ code: res.code, email, id: res.data.userId });
          navigate('/supplier/verify', { replace: true });
        }
      },
    }
  );

  const { mutate: verifyOTP, isPending: isVerifying } = usePost<{
    action: 'verify';
    otp: string;
    id: string;
  }>(apiMembers.post.verifyOTP, false, [loginId || getSignupResponse()?.id || ''], {
    onSuccess(data) {
      const res = data as {
        code: string;
        data?: {
          email: string;
          isNewUser: boolean;
          existingUserWithPortal: boolean;
          existingUserWithoutPortal: boolean;
          userAssociatedSupplierAccounts: UserAssociatedSupplierAccounts[];
          userId: string;
        };
      };
      if (res.code === 'email_verified' && res.data) {
        // Merge verification response with existing signup response
        const currentResponse = getSignupResponse();
        setSignupResponse({
          ...currentResponse,
          code: res.code,
          id: res.data.userId,
          ...res.data,
        });
        event('Supplier Email Verified', {
          objectType: 'Email',
          action: 'Update',
          targetType: 'Supplier Organization',
          businessContext: {
            email: currentResponse?.email,
            inviteCode: userData?.loginContext?.inviteCode,
          },
          source: 'OTP Input',
          page: 'Email Verification',
        });
        navigate('/supplier/signup', { replace: true });
      }
    },
  });

  const { mutate: signUp2ndStep, isPending: isPending2ndStep } = usePost<{
    id: string;
    loginId: string;
    userName: string;
    password: string;
    confirmPassword: string;
    companyName: string;
  }>(apiSuppliers.post.signup, false, [], {
    onSuccess(data) {
      const res = data as { code: string; userId: string; data: UserAssociatedSupplierAccounts };
      if (res.code === 'supplier_account_created') {
        // Federated build (test-o/prod) WITH a real Auth0 org id: complete login through Auth0
        // for the just-created org so AuthLoaderFederated resolves the org-scoped session and
        // lands on the dashboard (DL-60730 case 2507019) — the browser has no Auth0 session yet,
        // so the Forms-only companySignIn below would bounce to the Auth0 login screen. We pass
        // the REAL Auth0 Organizations id ("org_...") the backend returns; a reconstructed
        // "org-{legacyOrgId}" name is unreliable (the tenant mixes it with display-name slugs
        // like "delogue-garments-39"), and the org id is canonical.
        //
        // If auth0OrgId is absent — a non-federated build, OR a federated env where
        // Auth0OrgScopedTokensEnabled was rolled back at runtime so no Auth0 org was created —
        // fall through to the legacy companySignIn. Redirecting to Auth0 with no organization
        // would authenticate the user org-less and strand them (D2's picker shows no eligible
        // org for the new supplier), with no recoverable path (DL-60730 review, greptile P1).
        if (USE_FEDERATED_AUTH && res.data.auth0OrgId) {
          clearSignupResponse();
          loginWithRedirect({
            authorizationParams: { organization: res.data.auth0OrgId },
          });
          return;
        }
        companySignIn(res.data.supplierOrgId, res.data.supplierName, res.data.userId);
      }
    },
  });

  return {
    getSignupResponse,
    setSignupResponse,
    clearSignupResponse,
    signupResponse: getSignupResponse(),
    signUp,
    isPending,
    verifyOTP,
    isVerifying,
    signUp2ndStep,
    isPending2ndStep,
    companySignIn,
  };
};
